<script lang="ts">
  // One question's input control, shared by both QuestionnaireRenderer
  // layouts (wizard + form). Image questions own their whole upload
  // lifecycle here (immediate upload on pick, preview, replace/remove)
  // and report busy-ness up so the parent can lock navigation/submit.
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { uploadQuestionnaireImage } from '../../lib/convexApi';
  import type { AnswerValue, QuestionDefinition } from '../../lib/types';

  export let question: QuestionDefinition;
  export let value: AnswerValue | undefined = undefined;

  const dispatch = createEventDispatcher<{
    answer: AnswerValue | undefined;
    uploading: boolean;
  }>();

  function setAnswer(next: AnswerValue | undefined) {
    dispatch('answer', next);
  }

  function setFromEvent(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    setAnswer(target.value);
  }

  function setNumberFromEvent(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.value === '') {
      setAnswer(undefined);
      return;
    }
    setAnswer(Number(target.value));
  }

  function toggleMulti(option: string) {
    const current = Array.isArray(value) ? (value as string[]) : [];
    setAnswer(
      current.includes(option) ? current.filter(entry => entry !== option) : [...current, option]
    );
  }

  function sliderValue(): number {
    if (typeof value === 'number') return value;
    return question.min ?? 1;
  }

  // --- image: upload immediately on pick, answer with the storage id -------
  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
  let uploading = false;
  let uploadError: string | null = null;
  let previewUrl: string | null = null;

  onDestroy(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  });

  async function pickImage(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // re-picking the same file must re-fire change
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      uploadError = 'That file is not an image.';
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      uploadError = 'Photo is too large (max 10 MB).';
      return;
    }
    uploading = true;
    uploadError = null;
    dispatch('uploading', true);
    try {
      const storageId = await uploadQuestionnaireImage(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = URL.createObjectURL(file);
      setAnswer(storageId);
    } catch (err) {
      uploadError = err instanceof Error ? err.message : 'Upload failed — try again.';
    } finally {
      uploading = false;
      dispatch('uploading', false);
    }
  }

  function removeImage() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrl = null;
    }
    uploadError = null;
    setAnswer(undefined);
  }
</script>

{#if question.type === 'textarea'}
  <textarea
    class="qi__input qi__textarea"
    rows="4"
    value={typeof value === 'string' ? value : ''}
    placeholder={question.placeholder ?? ''}
    on:input={setFromEvent}
  ></textarea>
{:else if question.type === 'select'}
  <div class="qi__options">
    {#each question.options ?? [] as option (option.value)}
      <button
        type="button"
        class="qi__chip"
        class:qi__chip--selected={value === option.value}
        aria-pressed={value === option.value}
        on:click={() => setAnswer(option.value)}
      >
        {option.label}
      </button>
    {/each}
  </div>
{:else if question.type === 'multi-select'}
  <div class="qi__options">
    {#each question.options ?? [] as option (option.value)}
      {@const selected = Array.isArray(value) && value.includes(option.value)}
      <button
        type="button"
        class="qi__chip"
        class:qi__chip--selected={selected}
        aria-pressed={selected}
        on:click={() => toggleMulti(option.value)}
      >
        {option.label}
      </button>
    {/each}
  </div>
{:else if question.type === 'boolean'}
  <div class="qi__options">
    <button
      type="button"
      class="qi__chip"
      class:qi__chip--selected={value === true}
      aria-pressed={value === true}
      on:click={() => setAnswer(true)}
    >
      Yes
    </button>
    <button
      type="button"
      class="qi__chip"
      class:qi__chip--selected={value === false}
      aria-pressed={value === false}
      on:click={() => setAnswer(false)}
    >
      No
    </button>
  </div>
{:else if question.type === 'slider'}
  <div class="qi__slider">
    <input
      type="range"
      min={question.min ?? 1}
      max={question.max ?? 10}
      step={question.step ?? 1}
      value={sliderValue()}
      on:input={setNumberFromEvent}
    />
    <div class="qi__slider-labels">
      <span>{question.min ?? 1}</span>
      <span class="qi__slider-value">{sliderValue()}</span>
      <span>{question.max ?? 10}</span>
    </div>
  </div>
{:else if question.type === 'number'}
  <input
    class="qi__input"
    type="number"
    min={question.min}
    max={question.max}
    value={typeof value === 'number' ? value : ''}
    placeholder={question.placeholder ?? ''}
    on:input={setNumberFromEvent}
  />
{:else if question.type === 'image'}
  <div class="qi__image">
    {#if typeof value === 'string' && value}
      {#if previewUrl}
        <img class="qi__image-preview" src={previewUrl} alt="Your upload" />
      {:else}
        <p class="qi__image-attached">photo attached ✓</p>
      {/if}
      <div class="qi__image-actions">
        <label class="qi__chip qi__chip--file">
          {uploading ? 'Uploading…' : 'Replace photo'}
          <input type="file" accept="image/*" disabled={uploading} on:change={pickImage} />
        </label>
        <button type="button" class="qi__chip" disabled={uploading} on:click={removeImage}>
          Remove
        </button>
      </div>
    {:else}
      <label class="qi__chip qi__chip--file">
        {uploading ? 'Uploading…' : 'Choose a photo'}
        <input type="file" accept="image/*" disabled={uploading} on:change={pickImage} />
      </label>
    {/if}
    {#if uploadError}
      <p class="qi__error">{uploadError}</p>
    {/if}
  </div>
{:else}
  <input
    class="qi__input"
    type={question.type === 'email'
      ? 'email'
      : question.type === 'tel'
      ? 'tel'
      : question.type === 'url'
      ? 'url'
      : 'text'}
    value={typeof value === 'string' ? value : ''}
    placeholder={question.placeholder ?? ''}
    on:input={setFromEvent}
  />
{/if}

<style>
  .qi__input,
  .qi__textarea {
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
  }

  .qi__textarea {
    resize: vertical;
  }

  .qi__options {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .qi__chip {
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-secondary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    width: auto; /* the mobile system.css rule makes bare buttons 100% wide */
  }

  .qi__chip--selected {
    border-color: rgb(var(--accent-rgb) / 0.6);
    color: var(--color-text-amber);
    background: rgb(var(--accent-rgb) / 0.08);
  }

  .qi__slider {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .qi__slider input[type='range'] {
    width: 100%;
  }

  .qi__slider-labels {
    display: flex;
    justify-content: space-between;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }

  .qi__slider-value {
    color: var(--color-text-amber);
    font-weight: 600;
  }

  .qi__image {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    align-items: flex-start;
  }

  .qi__image-preview {
    max-width: min(100%, 280px);
    max-height: 280px;
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    object-fit: cover;
  }

  .qi__image-attached {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-amber);
  }

  .qi__image-actions {
    display: flex;
    gap: var(--space-2);
  }

  /* A label styled as a chip so the native file input stays accessible. */
  .qi__chip--file {
    display: inline-flex;
    align-items: center;
    position: relative;
  }

  .qi__chip--file input[type='file'] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
  }

  .qi__error {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-danger-400, #ff7676);
  }
</style>
